import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import DashboardLayout from '@/components/layout/DashboardLayout';
import SecuritySettingsPanel from '@/components/security/SecuritySettingsPanel';
import GdprSettingsPanel from '@/components/settings/GdprSettingsPanel';
import NotificationPreferencesPanel from '@/components/settings/NotificationPreferencesPanel';
import type { NotificationCategory } from '@/components/settings/NotificationPreferencesPanel';
import { SirenCard } from '@/components/settings/SirenCard';
import SubscriptionPanel from '@/components/settings/SubscriptionPanel';
import type { SubscriptionPlanConfig } from '@/components/settings/SubscriptionPanel';
import PageHeader from '@/components/ui/PageHeader';
import { clubSidebar } from '@/config/sidebarConfig.ts';
import {
  clubStripeBillingPortalApi,
  clubStripeSetupIntentApi,
  clubStripeSubscribeApi,
  getClubApi,
  updateClubApi,
} from '@/lib/api/clubsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { useSaasStore } from '@/stores/saas-store';
import { CLUB_PRO_ANNUAL_TTC_CENTS, CLUB_PRO_MONTHLY_TTC_CENTS } from '@shared/constants';
import { isClubPremium } from '@shared/utils/premium.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Check, Copy, CreditCard, Database, Eye, EyeOff, Gift, Settings, Shield } from 'lucide-react';
import { toast } from 'sonner';

// ─── Tab definitions ───────────────────────────────────────────

type TabId = 'security' | 'subscription' | 'referral' | 'notifications' | 'gdpr' | 'preferences';

interface TabDef {
  id: TabId;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  soon?: true;
}

const TABS: TabDef[] = [
  {
    id: 'security',
    label: 'Securite',
    description: '2FA, appareils de confiance',
    icon: Shield,
  },
  {
    id: 'subscription',
    label: 'Abonnement',
    description: 'Plan Club Pro',
    icon: CreditCard,
  },
  {
    id: 'referral',
    label: 'Parrainage',
    description: 'Code parrainage et recompenses',
    icon: Gift,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    description: 'Alertes email et push',
    icon: Bell,
  },
  {
    id: 'gdpr',
    label: 'Donnees personnelles',
    description: 'Export RGPD, suppression de compte',
    icon: Database,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    description: 'Visibilite du profil',
    icon: Settings,
  },
];

const DEFAULT_TAB: TabId = 'security';

const CLUB_NOTIFICATION_CATEGORIES: NotificationCategory[] = [
  {
    id: 'member_join',
    label: 'Nouvelles inscriptions',
    description: 'Un nouveau membre rejoint le club',
    email: true,
  },
  {
    id: 'membership_request',
    label: "Demandes d'adhesion",
    description: 'Un joueur demande a rejoindre le club',
    email: true,
  },
  {
    id: 'competition',
    label: 'Concours du club',
    description: 'Mises a jour des concours organises par le club',
    email: true,
  },
  {
    id: 'payments',
    label: 'Paiements et cotisations',
    description: 'Cotisations recues, rappels de paiement',
    email: true,
  },
  {
    id: 'platform',
    label: 'Actualites plateforme',
    description: 'Nouveautes, maintenances, mises a jour',
    email: false,
  },
];

// ─── Settings Nav ──────────────────────────────────────────────

interface SettingsNavProps {
  activeTab: TabId;
  onSelect: (tab: TabId) => void;
  tabs: TabDef[];
}

/**
 * Responsive behavior:
 * - Mobile: horizontal scroll “pill” nav (label only for active tab)
 * - Desktop: left vertical nav
 */
function SettingsNav({ activeTab, onSelect, tabs }: SettingsNavProps) {
  return (
    <>
      {/* Mobile nav */}
      <nav className='md:hidden -mx-4 sm:-mx-6 lg:mx-0 px-4 sm:px-6 lg:px-0 overflow-x-auto'>
        <div className='flex gap-2 pb-2'>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                type='button'
                onClick={() => !tab.soon && onSelect(tab.id)}
                disabled={tab.soon}
                className={`shrink-0 inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold transition ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                } ${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                <span className={`whitespace-nowrap ${isActive ? 'inline-block' : 'hidden md:inline-block'}`}>
                  {tab.label}
                </span>
                {tab.soon && (
                  <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                    bientot
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Desktop nav */}
      <nav className='hidden md:flex flex-col gap-1 w-64 shrink-0'>
        {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              type='button'
              onClick={() => !tab.soon && onSelect(tab.id)}
              disabled={tab.soon}
              className={`flex items-start gap-3 w-full px-3 py-3 rounded-lg text-left transition-colors group
								${isActive ? 'bg-emerald-50 text-emerald-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'}
								${tab.soon ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
							`}
            >
              <Icon
                className={`w-4 h-4 mt-0.5 shrink-0 ${
                  isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'
                }`}
              />
              <div className='min-w-0'>
                <div className='flex items-center gap-2'>
                  <span className={`text-sm font-semibold ${isActive ? 'text-emerald-700' : ''}`}>{tab.label}</span>
                  {tab.soon && (
                    <span className='text-[10px] font-bold uppercase tracking-wide bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded'>
                      bientot
                    </span>
                  )}
                </div>
                <p className='text-xs text-slate-400 mt-0.5 truncate'>{tab.description}</p>
              </div>
            </button>
          );
        })}
      </nav>
    </>
  );
}

// ─── Referral Panel ─────────────────────────────────────────────

function ReferralPanel() {
  const user = useAuthStore(s => s.user);
  const [copied, setCopied] = useState(false);

  function copyReferralCode() {
    if (!user?.referralCode) return;
    navigator.clipboard.writeText(user.referralCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center'>
            <Gift className='w-5 h-5 text-indigo-600' />
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900'>Mon code parrainage</h3>
            <p className='text-sm text-slate-500 truncate'>Parrainez des clubs et gagnez des recompenses</p>
          </div>
        </div>

        <p className='text-sm text-slate-600 mb-5 leading-relaxed max-w-2xl'>
          Partagez ce code unique avec des responsables de clubs. Chaque club qui s&apos;inscrit avec votre code et
          souscrit un abonnement Club Pro vous fait progresser dans les paliers de recompenses.
        </p>

        <div className='flex flex-col sm:flex-row sm:items-center gap-3 max-w-sm'>
          <input
            readOnly
            value={user?.referralCode ?? '—'}
            className='w-full px-4 py-3 border border-slate-200 rounded-xl bg-slate-50 font-mono text-xl font-extrabold tracking-widest text-slate-900 select-all cursor-text'
          />
          <button
            onClick={copyReferralCode}
            disabled={!user?.referralCode}
            title='Copier le code'
            aria-label='Copier le code de parrainage'
            className='w-full sm:w-12 cursor-pointer flex h-12 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-600 disabled:opacity-40'
          >
            {copied ? <Check className='w-5 h-5 text-emerald-500' /> : <Copy className='w-5 h-5' />}
          </button>
        </div>

        {copied && <p className='mt-2 text-sm font-semibold text-emerald-600'>Code copie dans le presse-papiers !</p>}
      </div>
    </div>
  );
}

// ─── Club Subscription Panel ──────────────────────────────────────

function ClubSubscriptionPanel() {
  const user = useAuthStore(s => s.user);
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const clubId = user?.clubId;
  const isSubscribed = isClubPremium(user?.club?.clubPlan);
  const queryClient = useQueryClient();

  // Gating D6 : le SIREN (chargé depuis le club complet) conditionne la souscription
  const { data: club, isLoading } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId ?? 0),
    queryFn: () => getClubApi(clubId!),
    enabled: !!clubId && isAuthenticated,
  });

  async function saveSiren(siren: string) {
    await updateClubApi(clubId!, { siren });
    await queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId ?? 0) });
  }

  const sirenMissing = !club?.siren;

  const planConfig: SubscriptionPlanConfig = {
    planName: 'Club Pro',
    description: 'Gerez votre club avec des outils avances : statistiques, export, gestion des licences FFPJP.',
    priceMonthly: `${CLUB_PRO_MONTHLY_TTC_CENTS / 100}`,
    priceAnnual: `${CLUB_PRO_ANNUAL_TTC_CENTS / 100}`,
    features: [
      'Gestion avancee des membres et licences',
      'Statistiques detaillees du club',
      'Export des donnees (XLSX, CSV)',
      'Support prioritaire',
    ],
    isSubscribed,
    onSetupIntent: () => clubStripeSetupIntentApi(clubId!),
    onSubscribe: (interval, paymentMethodId) => clubStripeSubscribeApi(clubId!, interval, paymentMethodId),
    onBillingPortal: isSubscribed && clubId ? () => clubStripeBillingPortalApi(clubId) : null,
    accentColor: 'emerald',
    invalidateKeys: clubId ? [queryKeys.clubs.detail(clubId)] : [],
    disabledReason:
      !isSubscribed && sirenMissing
        ? 'Renseignez le SIREN de votre club (ci-dessus) pour pouvoir souscrire — il est obligatoire pour la facturation.'
        : null,
  };

  if (!clubId) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
          <p className='text-sm text-slate-500'>Vous devez etre associe a un club pour gerer un abonnement Club Pro.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return <div className='max-w-2xl h-56 animate-pulse rounded-2xl bg-slate-100' />;
  }

  return (
    <div className='space-y-4 max-w-2xl'>
      {!isSubscribed && (
        <SirenCard
          key={club?.siren ?? 'empty'}
          siren={club?.siren ?? null}
          onSave={saveSiren}
          requiredFor="souscrire l'abonnement Club Pro"
          accentColor='emerald'
        />
      )}
      <SubscriptionPanel config={planConfig} />
    </div>
  );
}

// ─── Preferences Panel ────────────────────────────────────────

function PreferencesPanel() {
  const user = useAuthStore(s => s.user);
  const clubId = user?.clubId;
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const queryClient = useQueryClient();

  const { data: club } = useQuery({
    queryKey: queryKeys.clubs.detail(clubId!),
    queryFn: () => getClubApi(clubId!),
    enabled: !!clubId && isAuthenticated,
  });

  const isProfilePublic = club?.isProfilePublic ?? true;

  const toggleMutation = useMutation({
    mutationFn: (value: boolean) => updateClubApi(clubId!, { isProfilePublic: value }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clubs.detail(clubId!) });
      toast.success('Preferences mises à jour');
    },
    onError: (err: Error) => {
      toast.error(err.message ?? 'Erreur lors de la mise à jour');
    },
  });

  if (!clubId) {
    return (
      <div className='max-w-2xl'>
        <div className='bg-white rounded-2xl p-4 sm:p-6 border border-dashed border-slate-300'>
          <p className='text-sm text-slate-500'>Vous devez etre associe à un club pour gerer les preferences.</p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-6 max-w-2xl'>
      <div className='bg-white rounded-2xl p-4 sm:p-6 border border-slate-200'>
        <div className='flex items-center gap-3 mb-6'>
          <div className='w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center'>
            {isProfilePublic ? (
              <Eye className='w-5 h-5 text-emerald-600' />
            ) : (
              <EyeOff className='w-5 h-5 text-slate-400' />
            )}
          </div>
          <div className='min-w-0'>
            <h3 className='font-bold text-slate-900'>Visibilite du profil</h3>
            <p className='text-sm text-slate-500 truncate'>Controlez la visibilite de votre club sur le site public</p>
          </div>
        </div>

        <div className='space-y-4'>
          <div className='flex items-center justify-between gap-4 p-4 bg-slate-50 rounded-xl'>
            <div className='flex-1 min-w-0'>
              <div className='flex items-center gap-2'>
                <span className='font-semibold text-sm text-slate-900'>Profil club public</span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    isProfilePublic ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'
                  }`}
                >
                  {isProfilePublic ? 'visible' : 'masque'}
                </span>
              </div>
              <p className='text-xs text-slate-500 mt-1'>
                {isProfilePublic
                  ? 'Votre club apparait dans la recherche et son profil est accessible publiquement.'
                  : 'Votre club est masque de la recherche. Les visiteurs voient un message "profil prive".'}
              </p>
            </div>

            <button
              type='button'
              onClick={() => toggleMutation.mutate(!isProfilePublic)}
              disabled={toggleMutation.isPending}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none disabled:opacity-50 ${
                isProfilePublic ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
              aria-label='Basculer la visibilite du profil'
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isProfilePublic ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Panel content ─────────────────────────────────────────────

function TabContent({ tab }: { tab: TabId }) {
  switch (tab) {
    case 'security':
      return <SecuritySettingsPanel />;
    case 'subscription':
      return <ClubSubscriptionPanel />;
    case 'referral':
      return <ReferralPanel />;
    case 'notifications':
      return <NotificationPreferencesPanel categories={CLUB_NOTIFICATION_CATEGORIES} accentColor='emerald' />;
    case 'gdpr':
      return <GdprSettingsPanel accentColor='emerald' />;
    case 'preferences':
      return <PreferencesPanel />;
    default:
      return null;
  }
}

// ─── Main ──────────────────────────────────────────────────────

export default function ClubSettings() {
  const referralEnabled = useSaasStore(s => s.referralEnabled);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') as TabId | null;

  const tabs = referralEnabled ? TABS : TABS.filter(t => t.id !== 'referral');
  const activeTab: TabId = rawTab && tabs.some(t => t.id === rawTab) ? rawTab : DEFAULT_TAB;

  function handleSelect(tab: TabId) {
    setSearchParams({ tab }, { replace: true });
  }

  const currentTab = tabs.find(t => t.id === activeTab)!;

  return (
    <DashboardLayout sidebarConfig={clubSidebar}>
      <div className='p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto'>
        <PageHeader title='Parametres' subtitle='Configuration et securite de votre espace club' />

        {/* Layout responsive:
					- Mobile: horizontal nav + stacked content
					- Desktop: left nav + divider + content
				*/}
        <div className='mt-6 flex flex-col md:flex-row md:gap-8'>
          {/* Left nav */}
          <div className='md:shrink-0'>
            <SettingsNav activeTab={activeTab} onSelect={handleSelect} tabs={tabs} />
          </div>

          {/* Divider desktop only */}
          <div className='hidden md:block w-px bg-slate-200 self-stretch' />

          {/* Content */}
          <div className='flex-1 min-w-0 mt-4 md:mt-0'>
            <div className='mb-6'>
              <h2 className='text-xl font-bold text-slate-900'>{currentTab.label}</h2>
              <p className='text-sm text-slate-500 mt-0.5'>{currentTab.description}</p>
            </div>

            <TabContent tab={activeTab} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
